export interface ProductVariant {
  weight: string;
  price: number;
  stock: number;
  sku: string;
}

export interface BulkTier {
  minQty: number;
  discountPercent: number;
}

export interface Product {
  id: string;
  name: string;
  tamilName?: string;
  slug: string;
  category: string;
  mainCategory?: any;
  hsnCode: string;
  shortDesc: string;
  description: string;
  ingredients: string[];
  shelfLife: string;
  storage: string;
  isVegetarian: boolean;
  isBestseller?: boolean;
  isFestiveSpecial?: boolean;
  image: string;
  rating: number;
  reviewsCount: number;
  variants: ProductVariant[];
  bulkTiers: BulkTier[];
}

export interface OccasionPackage {
  id: string;
  _id?: string;
  slug?: string;
  title: string;
  tamilTitle?: string;
  occasionCategory: string;
  moduleType?: string;
  occasion: string;
  subtitle: string;
  description: string;
  image: string;
  badge: string;
  minOrderQty: number;
  basePrice: number;
  originalPrice?: number;
  includedSnacks: string[];
  includedProducts?: Array<{
    productId?: any;
    name: string;
    sku?: string;
    image?: string;
    price?: number;
    weight?: string;
    quantity?: number;
  }>;
  customQuestions?: Array<{
    id: string;
    question: string;
    type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio';
    options?: string[];
    placeholder?: string;
    required?: boolean;
  }>;
  packagingType: string;
  idealFor: string[];
  customizationSupported: boolean;
  shelfLife?: string;
  isFestiveSpecial?: boolean;
  isBestseller?: boolean;
}

export interface Quotation {
  id: string;
  quotationNumber: string;
  clientName: string;
  companyName?: string;
  email: string;
  phone: string;
  occasionOrEvent?: string;
  orderType: 'BULK' | 'CORPORATE' | 'CUSTOM_BOX' | 'EXPORT';
  items: Array<{
    name: string;
    variant: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  subtotal: number;
  bulkDiscountPercent: number;
  bulkDiscountAmount: number;
  customizationFee: number;
  deliveryFee: number;
  gstAmount: number;
  totalAmount: number;
  status: 'PENDING_REVIEW' | 'QUOTATION_SENT' | 'ACCEPTED' | 'REJECTED' | 'CONVERTED_TO_ORDER';
  validUntil: string;
  deliveryDateRequired: string;
  destinationCity: string;
  deliveryLocations?: Array<{ city: string; quantity: number; address: string }>;
  createdAt: string;
  notes?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  channel: 'B2C_RETAIL' | 'B2B_WHOLESALE' | 'CORPORATE' | 'CUSTOM_BOX' | 'EXPORT';
  items: Array<{
    productId: string;
    name: string;
    weight: string;
    quantity: number;
    price: number;
    hsnCode?: string;
  }>;
  shippingAddress: {
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  deliveryLocations?: Array<{ city: string; quantity: number; address: string }>;
  paymentMethod: 'ONLINE_UPI' | 'CREDIT_CARD' | 'NET_BANKING' | 'COD' | 'B2B_INVOICE';
  paymentStatus: 'PAID' | 'PENDING' | 'FAILED';
  orderStatus: 'PLACED' | 'CONFIRMED' | 'PACKING' | 'DISPATCHED' | 'OUT_FOR_DELIVERY' | 'DELIVERED';
  subtotal: number;
  deliveryFee: number;
  taxAmount: number;
  cgst?: number;
  sgst?: number;
  totalAmount: number;
  createdAt: string;
  invoiceNumber?: string;
  invoiceUrl?: string;
  trackingSteps?: Array<{ title: string; date: string; done: boolean; current?: boolean }>;
}

export const BOX_BUILDER_OPTIONS = {
  sizes: [
    { id: 'box-small', name: 'Classic Mini (3 Snacks)', capacity: 3, baseCost: 120, dimensions: '22 x 15 x 7 cm' },
    { id: 'box-medium', name: 'Heritage Grand (5 Snacks)', capacity: 5, baseCost: 180, dimensions: '30 x 22 x 8 cm' },
    { id: 'box-royal', name: 'Kathakali Royal Trunk (8 Snacks)', capacity: 8, baseCost: 290, dimensions: '40 x 28 x 10 cm' }
  ],
  finishes: [
    { id: 'finish-vermilion', name: 'Royal Vermilion & Gold Rigid Box', cost: 60, icon: 'Shield' },
    { id: 'finish-tin', name: 'Vintage Heritage Embossed Tin', cost: 95, icon: 'Box' },
    { id: 'finish-palm', name: 'Traditional Eco-Weave Palm Craft Box', cost: 45, icon: 'Leaf' },
    { id: 'finish-kraft', name: 'Minimalist Sustainable Earth Kraft Box', cost: 30, icon: 'Feather' }
  ],
  brandingOptions: [
    { id: 'brand-none', name: 'Standard Elavarasi Foods Heritage Seal', cost: 0 },
    { id: 'brand-custom-sticker', name: 'Custom Printed Event Sticker / Monogram', cost: 25 },
    { id: 'brand-gold-foil-logo', name: 'Metallic Gold Foil Company Logo Emboss', cost: 60 }
  ],
  greetingCards: [
    { id: 'card-wedding', name: 'Traditional Wedding Blessing Card', cost: 20 },
    { id: 'card-diwali', name: 'Auspicious Deepavali Greeting Card', cost: 20 },
    { id: 'card-corporate', name: 'Executive Corporate Gratitude Letter', cost: 25 },
    { id: 'card-blank', name: 'No Custom Card', cost: 0 }
  ]
};

// All mock data arrays removed in favor of live backend database connections
export const CATEGORIES: Array<{ id: string; name: string; count: number }> = [];
export const PRODUCTS: Product[] = [];
export const OCCASIONS: OccasionPackage[] = [];
export const INITIAL_QUOTATIONS: Quotation[] = [];
export const INITIAL_ORDERS: Order[] = [];
